import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  UsersIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA ---
const mockCourses = [
  { id: 1, title: 'Introduction to Agile Project Management', category: 'Project Management', instructor: 'Sanjay Kumar', description: 'Learn the fundamentals of Agile methodologies and Scrum framework.', enrolled: true },
  { id: 2, title: 'Advanced Python for Data Science', category: 'Technical Skills', instructor: 'Priya Sharma', description: 'Master data analysis and visualization with advanced Python libraries.', enrolled: false },
  { id: 3, title: 'Effective Communication in the Workplace', category: 'Soft Skills', instructor: 'Meera Deshmukh', description: 'Develop verbal and non-verbal communication skills for professional success.', enrolled: true },
  { id: 4, title: 'Cloud Computing Fundamentals (AWS)', category: 'Technical Skills', instructor: 'Rajiv Sinha', description: 'An introductory course covering core concepts of cloud computing and AWS services.', enrolled: false },
  { id: 5, title: 'Leadership and Team Building', category: 'Leadership', instructor: 'Geeta Patel', description: 'Equip yourself with the skills to lead and motivate high-performing teams.', enrolled: false },
];

const mockUpcomingSessions = [
  { id: 1, course: 'Introduction to Agile Project Management', date: '2025-10-01', time: '10:00 AM' },
  { id: 2, course: 'Advanced Python for Data Science', date: '2025-10-15', time: '02:00 PM' },
  { id: 3, course: 'Effective Communication in the Workplace', date: '2025-10-05', time: '09:00 AM' },
];
// --- END: MOCK DATA ---

const TrainingDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = useMemo(() => ['All', ...new Set(mockCourses.map(c => c.category))], []);
  const enrolledEmployeesCount = useMemo(() => new Set(mockCourses.filter(c => c.enrolled).map(c => c.instructor)).size, []);
  
  const filteredCourses = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return mockCourses.filter(course =>
      (selectedCategory === 'All' || course.category === selectedCategory) &&
      (course.title.toLowerCase().includes(term) || course.description.toLowerCase().includes(term))
    );
  }, [searchTerm, selectedCategory]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-sky-200 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-4xl font-extrabold text-indigo-800">Training Dashboard</h1>
        <p className="text-xl font-light text-indigo-600 mt-2">
          Discover and manage training programs.
        </p>
      </motion.div>

      {/* Dashboard Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-7xl mx-auto">
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-indigo-100">
              <BookOpenIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{mockCourses.length}</h3>
              <p className="text-sm text-gray-500">Total Courses</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-cyan-100">
              <UsersIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{enrolledEmployeesCount}</h3>
              <p className="text-sm text-gray-500">Enrolled Employees</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-sky-100">
              <CalendarDaysIcon className="h-8 w-8 text-sky-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{mockUpcomingSessions.length}</h3>
              <p className="text-sm text-gray-500">Upcoming Sessions</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content: Course Catalog and Upcoming Sessions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Course Catalog */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Course Catalog</h3>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="py-2 px-4 rounded-full bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <AnimatePresence>
              {filteredCourses.length > 0 ? (
                filteredCourses.map(course => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="p-4 rounded-2xl bg-gray-50 border border-gray-200"
                  >
                    <h4 className="font-bold text-lg text-gray-900">{course.title}</h4>
                    <span className="text-sm text-indigo-600 font-medium">{course.category}</span>
                    <p className="text-sm text-gray-600 mt-2">{course.description}</p>
                    <div className="flex justify-between items-center mt-4">
                      <p className="text-xs text-gray-400">Instructor: {course.instructor}</p>
                      <button className="py-2 px-4 rounded-full text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors">
                        {course.enrolled ? 'View Details' : 'Enroll Now'}
                      </button>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-2 text-center py-10 text-gray-500">
                  No courses found matching your criteria.
                </div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Upcoming Sessions */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.5 }}
          className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Upcoming Sessions</h3>
          <ul className="divide-y divide-gray-200">
            {mockUpcomingSessions.map(session => (
              <li key={session.id} className="py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{session.course}</p>
                  <p className="text-xs text-gray-500">{moment(session.date).format('MMMM D, YYYY')} at {session.time}</p>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default TrainingDashboard;